import { useRef, useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AgentMode } from "../../services/chat-provider";
import { ModeInfos } from "../../constants/modeInfos";

export default function SampleTaskCards({ mode = AgentMode.DEFAULT, onTaskClick }) {
  const scrollContainerRef = useRef(null);
  const autoScrollTimeoutRef = useRef(null);
  const resumeTimeoutRef = useRef(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);

  const config = ModeInfos[mode] || ModeInfos[AgentMode.DEFAULT];
  const tasks = config.tasks || [];

  // Dimensions for infinite scroll
  const cardWidth = 288 + 16; // w-72 (288px) + gap-4 (16px)
  const singleSetWidth = tasks.length * cardWidth;

  // Triple the tasks for infinite scroll effect
  const infiniteTasks = [...tasks, ...tasks, ...tasks];

  // Handle scroll wrapping for infinite effect
  const handleScrollWrap = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || tasks.length === 0) return;

    const { scrollLeft } = container;

    // Wrap from end to middle set
    if (scrollLeft >= singleSetWidth * 2 - 10) {
      container.scrollLeft = singleSetWidth;
    }
    // Wrap from start to middle set
    else if (scrollLeft <= 10) {
      container.scrollLeft = singleSetWidth;
    }
  }, [singleSetWidth, tasks.length]);

  // Scroll cards left or right
  const scrollCards = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;

    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  };

  // Auto-scroll logic with infinite loop
  const runAutoScroll = useCallback(() => {
    if (!scrollContainerRef.current || tasks.length === 0) return;

    const { scrollLeft } = scrollContainerRef.current;

    // If past middle set end, instantly jump back to middle set start
    if (scrollLeft >= singleSetWidth * 2) {
      scrollContainerRef.current.scrollLeft = singleSetWidth;
    }

    // Continue scrolling incrementally
    scrollContainerRef.current.scrollBy({ left: 1, behavior: 'auto' });
    // Queue next frame (30ms interval creates smooth animation)
    autoScrollTimeoutRef.current = setTimeout(runAutoScroll, 30);
  }, [singleSetWidth, tasks.length]);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollTimeoutRef.current) {
      clearTimeout(autoScrollTimeoutRef.current);
      autoScrollTimeoutRef.current = null;
    }
  }, []);

  const startAutoScroll = useCallback(() => {
    stopAutoScroll();
    runAutoScroll();
  }, [runAutoScroll, stopAutoScroll]);

  // User interaction handlers
  const handleUserInteraction = useCallback(() => {
    // Clear any pending resume timeout
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
    setIsAutoScrolling(false);
  }, []);

  const handleUserLeave = useCallback(() => {
    // Clear any existing resume timeout
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
    }
    // Resume auto-scroll after 500ms delay (cooldown)
    resumeTimeoutRef.current = setTimeout(() => {
      setIsAutoScrolling(true);
      resumeTimeoutRef.current = null;
    }, 500);
  }, []);

  // Initialize scroll to middle set on mount and when mode/tasks change
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || tasks.length === 0) return;

    container.scrollLeft = singleSetWidth;

    // Safari doesn't always repaint after a programmatic scrollLeft change,
    // causing cards to be invisible until the user interacts. Force a reflow
    // by reading offsetWidth (a layout-triggering property) then toggling a
    // no-op style so Safari schedules a paint.
    // eslint-disable-next-line no-unused-expressions
    container.offsetWidth;
    container.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      container.style.overflow = '';
    });
  }, [mode, tasks, singleSetWidth]);

  // Control auto-scroll
  useEffect(() => {
    if (isAutoScrolling) {
      startAutoScroll();
    } else {
      stopAutoScroll();
    }
    return stopAutoScroll;
  }, [isAutoScrolling, startAutoScroll, stopAutoScroll]);

  const handleTaskClick = (task) => {
    if (onTaskClick) {
      onTaskClick(task);
    } else {
      console.log('Task clicked:', task);
    }
  };

  if (!tasks || tasks.length === 0) return null;

  return (
    <div
      className="relative w-full max-w-4xl px-4"
      onMouseEnter={handleUserInteraction}
      onMouseLeave={handleUserLeave}
      onTouchStart={handleUserInteraction}
      onTouchEnd={handleUserLeave}
    >
      {/* Left Gradient & Arrow */}
      <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-background to-transparent z-10 flex items-center justify-center">
        <button
          onClick={() => scrollCards('left')}
          className="p-2 rounded-full bg-background border border-border hover:bg-muted transition-all duration-200"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-5 h-5 text-muted-foreground" />
        </button>
      </div>

      {/* Right Gradient & Arrow */}
      <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-background to-transparent z-10 flex items-center justify-center">
        <button
          onClick={() => scrollCards('right')}
          className="p-2 rounded-full bg-background border border-border hover:bg-muted transition-all duration-200"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </button>
      </div>

      {/* Scrollable Container */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScrollWrap}
        onWheel={handleUserInteraction}
        className="flex gap-4 overflow-x-auto px-1 py-2"
        style={{
          willChange: 'transform',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <style jsx>{`
          div::-webkit-scrollbar {
            display: none;
          }
        `}</style>

        {infiniteTasks.map((task, index) => {
          const TaskIcon = task.icon;
          // Only animate cards in the initially visible middle set
          const isInMiddleSet = index >= tasks.length && index < tasks.length * 2;
          const animationIndex = index % tasks.length;
          return (
            <motion.button
              key={`${index}-${task.title}`}
              initial={isInMiddleSet ? { y: 20, opacity: 0 } : { y: 0, opacity: 1 }}
              animate={{ y: 0, opacity: 1 }}
              transition={isInMiddleSet ? { duration: 0.5, delay: 0.3 + animationIndex * 0.1 } : { duration: 0 }}
              onClick={() => handleTaskClick(task)}
              className="group relative flex-shrink-0 w-72 h-24 overflow-hidden rounded-2xl bg-muted/80 border border-border/50 p-3 text-left transition-all duration-200 hover:bg-muted"
            >
              <div className="flex items-start gap-2 h-full">
                <div className="flex-shrink-0 text-xl">
                  <TaskIcon className={`w-5 h-5 ${task.color} transition-colors`} />
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-center h-full">
                  <h3 className="font-medium text-foreground text-sm mb-1">
                    {task.title}
                  </h3>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {task.text}
                  </p>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
